import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { type FC, Fragment, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Headline, Label, Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  type EvalPromptEditExample,
  type EvalPromptTheme,
  type GetEvalResultsParams,
  useGetEvalResults,
  useGetEvalResultsScopeOptions,
} from "@api/rest/types-and-hooks";
import { AcceptedSuggestions } from "@pages/Evaluations/AcceptedSuggestions";
import { PromptLink } from "@pages/Evaluations/PromptLink";
import { PromptSuggestions } from "@pages/Evaluations/PromptSuggestions";
import {
  EVAL_RESULTS_ORG_PARAM,
  EVAL_RESULTS_TEMPLATE_PARAM,
  METRICS_WINDOW_LABEL,
  ProductionSort,
  type ProductionSortValue,
  SortDirection,
  type SortDirectionValue,
  WORST_FIRST,
  formatPercent,
  idParam,
  productionRows,
  productionSummary,
  scopeTemplateLabel,
  scopeTemplates,
  sortProductionRows,
  themeSupportLabel,
} from "@pages/Evaluations/evalResultsProduction";

const formatWhen = (value: string): string =>
  new Date(value).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const SELECT_CLASSES =
  "text-sm border border-border-subtle rounded px-2 py-1.5 bg-bg-default " +
  "focus:outline-none focus:ring-1 focus:ring-limestone-900";

const EDITS_DASHBOARD_URL =
  "https://app.snowflake.com/jgwfgmr/ncb81949/#/streamlit-apps/ANALYTICS.STREAMLIT_APPS.RB_METRICS_DASH";

// Examples come from prod reports, so links always go to prod portal.
// Same routing as portal's reportPageUrl (AdminAnalytics/views/ReportsView.tsx).
const PORTAL_BASE_URL = "https://portal.fronterahealth.com";

const portalReportUrl = (reportId: string, reportType: string | null): string => {
  // Portal routes take the Relay global id, not the database id.
  const globalId = btoa(`ReportType:${reportId}`);
  switch (reportType) {
    case "REASSESSMENT":
      return `${PORTAL_BASE_URL}/clinical-reports/reassessment/${globalId}`;
    case "DIAGNOSIS_EVALUATION":
      return `${PORTAL_BASE_URL}/clinical-reports/evaluations/${globalId}`;
    default:
      return `${PORTAL_BASE_URL}/clinical-reports/assessment-reports/${globalId}`;
  }
};

const EditExample: FC<{ example: EvalPromptEditExample }> = ({ example }) => (
  <div className="flex flex-col gap-y-1 border-t border-border-subtle pt-2 first:border-t-0 first:pt-0">
    {example.reason ? <p className="text-text-secondary">{example.reason}</p> : null}
    {example.ai_text ? (
      <div>
        <div className="text-text-tertiary">AI wrote</div>
        <p className="mt-0.5 whitespace-pre-wrap text-text-primary">{example.ai_text}</p>
      </div>
    ) : null}
    {example.user_text ? (
      <div>
        <div className="text-text-tertiary">Clinician changed it to</div>
        <p className="mt-0.5 whitespace-pre-wrap text-text-primary">{example.user_text}</p>
      </div>
    ) : null}
    {example.report_id ? (
      <a
        href={portalReportUrl(example.report_id, example.report_type)}
        target="_blank"
        rel="noreferrer"
        className="self-start text-rio-700 underline decoration-dotted underline-offset-2 hover:text-rio-800"
      >
        Open report {example.report_id} in portal
      </a>
    ) : null}
  </div>
);

/** The top edit category, opening its suggested prompt change and example
 *  edits on click. A click popover, not a hover: the texts run to 500 chars,
 *  need selecting, and the anchored panel portals out of the table's scroll
 *  box, which clips CSS bubbles. */
const CategoryExamples: FC<{
  category: string;
  description: string | null;
  promptAction: string | null;
  examples: EvalPromptEditExample[];
}> = ({ category, description, promptAction, examples }) => (
  <Popover>
    <PopoverButton className="text-left underline decoration-dotted underline-offset-2 hover:text-text-primary focus:outline-none">
      {category}
    </PopoverButton>
    <PopoverPanel
      anchor={{ to: "bottom end", gap: 4 }}
      className="z-50 flex max-h-[32rem] w-[28rem] flex-col gap-y-2 overflow-y-auto rounded-md border border-border-subtle bg-bg-surface p-3 text-xs shadow-lg"
    >
      {description ? <p className="text-text-secondary">{description}</p> : null}
      {promptAction ? (
        <div className="rounded border border-border-subtle bg-bg-default px-2 py-1.5">
          <div className="text-text-tertiary">Suggested prompt change</div>
          <p className="mt-0.5 text-text-primary">{promptAction}</p>
        </div>
      ) : null}
      {examples.map((example, i) => (
        <EditExample key={`${example.report_id ?? "none"}-${i}`} example={example} />
      ))}
      {examples.length > 0 ? <p className="text-text-tertiary">Example edits from real reports.</p> : null}
    </PopoverPanel>
  </Popover>
);

/** One recurring edit pattern: why clinicians edit, beyond the category. */
const ThemeCard: FC<{ theme: EvalPromptTheme; orgScoped: boolean }> = ({ theme, orgScoped }) => (
  <div className="flex flex-col gap-y-1.5 rounded-md border border-border-subtle bg-bg-surface px-3 py-2.5">
    <div className="flex items-baseline justify-between gap-x-3">
      <span className="font-medium text-text-primary">{theme.title}</span>
      <span className="shrink-0 text-xs text-text-tertiary">{themeSupportLabel(theme, orgScoped)}</span>
    </div>
    <p className="whitespace-pre-wrap text-xs text-text-secondary">{theme.description}</p>
    {theme.categories.length > 0 ? (
      <div className="flex flex-wrap gap-1">
        {theme.categories.map((category) => (
          <span
            key={category}
            className="rounded border border-border-subtle bg-bg-default px-1.5 py-0.5 text-xs text-text-secondary"
          >
            {category}
          </span>
        ))}
      </div>
    ) : null}
  </div>
);

interface ScopeFiltersProps {
  orgId: number | null;
  templateId: number | null;
  onOrgChange: (orgId: number | null) => void;
  onTemplateChange: (templateId: number | null) => void;
}

/** Org + template scope, offering only what has results: orgs with
 *  production rows or suggestions, and templates with production rows, for
 *  the org picked or any org. */
const ScopeFilters: FC<ScopeFiltersProps> = ({ orgId, templateId, onOrgChange, onTemplateChange }) => {
  const { data: optionsResponse } = useGetEvalResultsScopeOptions();
  const options = optionsResponse?.status === 200 ? optionsResponse.data : null;
  const orgs = options?.organizations ?? [];
  const templates = useMemo(() => scopeTemplates(options?.templates ?? [], orgId), [options, orgId]);

  return (
    <div className="flex items-center gap-x-3">
      <select
        value={orgId?.toString() ?? ""}
        // The parent clears the template too: the template list is org-scoped.
        onChange={(e) => onOrgChange(e.target.value ? Number(e.target.value) : null)}
        className={SELECT_CLASSES}
      >
        <option value="">All organizations</option>
        {orgs.map((org) => (
          <option key={org.id} value={String(org.id)}>
            {org.name}
          </option>
        ))}
      </select>
      <select
        value={templateId?.toString() ?? ""}
        onChange={(e) => onTemplateChange(e.target.value ? Number(e.target.value) : null)}
        className={SELECT_CLASSES}
      >
        <option value="">All templates</option>
        {templates.map((t) => (
          <option key={t.id} value={t.id}>
            {scopeTemplateLabel(t)}
          </option>
        ))}
      </select>
    </div>
  );
};

interface ResultsTabProps {
  orgId: number | null;
  templateId: number | null;
}

/** Who owns the prompt: an org's name, or Global when no org does. */
const OwnerChip: FC<{ owner: string | null }> = ({ owner }) => (
  <span
    className="ml-2 rounded border border-border-subtle bg-bg-default px-1.5 py-0.5 text-xs text-text-secondary"
    title={owner ? `Owned by ${owner}` : "Global prompt, not owned by an organization"}
  >
    {owner ?? "Global"}
  </span>
);

const SummaryTile: FC<{ value: string; label: string; hint?: string }> = ({ value, label, hint }) => (
  <div className="rounded-lg border border-border-subtle bg-bg-surface px-4 py-3" title={hint}>
    <div className="text-2xl font-semibold text-text-primary">{value}</div>
    <div className="mt-0.5 text-xs text-text-tertiary">{label}</div>
  </div>
);

interface SortHeaderProps {
  column: ProductionSortValue;
  label: string;
  hint?: string;
  sort: { key: ProductionSortValue; direction: SortDirectionValue };
  onSort: (column: ProductionSortValue) => void;
  align?: "left" | "right";
}

/** Click to sort worst-first; click again to flip. */
const SortHeader: FC<SortHeaderProps> = ({ column, label, hint, sort, onSort, align = "right" }) => {
  const active = sort.key === column;
  const arrow = active ? (sort.direction === SortDirection.Desc ? " ▼" : " ▲") : "";
  return (
    <th className={`pb-1 font-normal ${align === "right" ? "pl-2 text-right" : "pr-6 text-left"}`} title={hint}>
      <button
        type="button"
        onClick={() => onSort(column)}
        aria-sort={active ? (sort.direction === SortDirection.Desc ? "descending" : "ascending") : "none"}
        className={`cursor-pointer hover:text-text-primary ${active ? "font-medium text-text-primary" : ""}`}
      >
        {label}
        {arrow}
      </button>
    </th>
  );
};

/** Production edits only: how clinicians change AI text in real reports.
 *  Test-bed checks and scores live on each prompt's Evaluations page. */
const ResultsTab: FC<ResultsTabProps> = ({ orgId, templateId }) => {
  const params: GetEvalResultsParams = {
    ...(orgId !== null ? { organization: orgId } : {}),
    ...(templateId !== null ? { template: templateId } : {}),
  };
  const orgScoped = orgId !== null;
  const scoped = orgScoped || templateId !== null;
  const { data, isFetching } = useGetEvalResults(params);

  // The Orval fetcher resolves on 4xx — check status before reading the body.
  const results = data?.status === 200 ? data.data : undefined;

  const [sort, setSort] = useState<{ key: ProductionSortValue; direction: SortDirectionValue }>({
    key: ProductionSort.Edited,
    direction: WORST_FIRST[ProductionSort.Edited],
  });
  const [expandedThemes, setExpandedThemes] = useState<Set<string>>(new Set());

  const production = useMemo(() => productionRows(results?.rankings ?? []), [results]);
  const sortedProduction = useMemo(() => sortProductionRows(production, sort.key, sort.direction), [production, sort]);
  const summary = useMemo(() => productionSummary(production), [production]);

  // One pipeline run writes every row, so the latest stamp dates them all.
  const productionComputedAt = useMemo(
    () =>
      production.reduce<string | null>((latest, row) => {
        const at = row.production.computed_at;
        return latest === null || at > latest ? at : latest;
      }, null),
    [production],
  );

  const onSort = (column: ProductionSortValue) =>
    setSort((prev) =>
      prev.key === column
        ? {
            key: column,
            direction: prev.direction === SortDirection.Desc ? SortDirection.Asc : SortDirection.Desc,
          }
        : { key: column, direction: WORST_FIRST[column] },
    );

  const toggleThemes = (promptKey: string) =>
    setExpandedThemes((prev) => {
      const next = new Set(prev);
      if (next.has(promptKey)) next.delete(promptKey);
      else next.add(promptKey);
      return next;
    });

  // Suggestions cover the whole org, not a template, and come from their own
  // weekly run: they show even when the production table is empty. Those
  // still to review need an org; accepted ones show for one org or all.
  const suggestions = (
    <>
      {orgId !== null ? <PromptSuggestions key={orgId} orgId={orgId} /> : null}
      <AcceptedSuggestions orgId={orgId} />
    </>
  );

  // Content-only early returns — the shell already rendered the filters.
  if (!results) {
    const status = (
      <Paragraph displayType="normal" size="medium" className="text-text-secondary">
        {isFetching ? "Loading…" : "Could not load results."}
      </Paragraph>
    );
    // Not while loading: the card would jump when the table arrives.
    return isFetching ? (
      status
    ) : (
      <div className="flex flex-col gap-y-6">
        {suggestions}
        {status}
      </div>
    );
  }

  if (production.length === 0) {
    return (
      <div className="flex flex-col gap-y-4">
        <Headline size="medium">Eval results</Headline>
        {suggestions}
        <div className="rounded-lg border border-border-subtle bg-bg-surface px-6 py-10 text-center">
          <Paragraph displayType="normal" size="medium" className="text-text-secondary">
            {scoped
              ? "No production data for this organization or template yet."
              : "Nothing here yet. Production edit metrics arrive nightly once reports use these prompts."}
          </Paragraph>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-y-6">
      <div className="flex items-end justify-between gap-x-6">
        <div>
          <Headline size="medium">Eval results</Headline>
          <Paragraph displayType="normal" size="small" className="mt-1 max-w-2xl text-text-secondary">
            How clinicians edit what the AI writes in real reports, over the {METRICS_WINDOW_LABEL}.
          </Paragraph>
          <Paragraph displayType="normal" size="small" className="mt-1 text-text-tertiary">
            For more edit information, see the{" "}
            <a
              href={EDITS_DASHBOARD_URL}
              target="_blank"
              rel="noreferrer"
              className="text-rio-700 underline decoration-dotted underline-offset-2 hover:text-rio-800"
            >
              Report Builder Metrics dashboard
            </a>
            .
          </Paragraph>
        </div>
        {productionComputedAt ? (
          <span className="shrink-0 text-xs text-text-tertiary">Last computed {formatWhen(productionComputedAt)}</span>
        ) : null}
      </div>

      {summary ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <SummaryTile value={summary.prompts.toLocaleString()} label="prompts in production" />
          <SummaryTile value={summary.fields.toLocaleString()} label="fields measured" />
          <SummaryTile
            value={formatPercent(summary.edited)}
            label={`of fields edited, ${METRICS_WINDOW_LABEL}`}
            hint="Share of AI-written fields a clinician changed at all, weighted by fields"
          />
          <SummaryTile
            value={summary.quality.toFixed(2)}
            label={`average quality, ${METRICS_WINDOW_LABEL}`}
            hint="0–1, higher is better, weighted by fields"
          />
        </div>
      ) : null}

      {suggestions}

      <section className="rounded-lg border border-border-subtle bg-bg-surface p-4">
        <div className="flex items-baseline justify-between gap-x-4">
          <Label size="small" colorType="secondary" className="uppercase tracking-wide">
            Production
          </Label>
          <span className="text-xs text-text-tertiary">
            real reports, {METRICS_WINDOW_LABEL} · click a column to sort
          </span>
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-text-tertiary">
                <SortHeader column={ProductionSort.Prompt} label="Prompt" sort={sort} onSort={onSort} align="left" />
                <SortHeader column={ProductionSort.Fields} label="Fields" sort={sort} onSort={onSort} />
                <SortHeader
                  column={ProductionSort.Edited}
                  label="Edited"
                  hint="Share of fields a clinician edited at all"
                  sort={sort}
                  onSort={onSort}
                />
                <SortHeader
                  column={ProductionSort.EditSize}
                  label="Edit size"
                  hint="How much of the AI text was changed, on average"
                  sort={sort}
                  onSort={onSort}
                />
                <SortHeader
                  column={ProductionSort.Rewrite}
                  label="Rewritten"
                  hint="Share of fields a clinician rewrote outright rather than edited"
                  sort={sort}
                  onSort={onSort}
                />
                <SortHeader
                  column={ProductionSort.Quality}
                  label="Quality"
                  hint="0–1, higher is better"
                  sort={sort}
                  onSort={onSort}
                />
                <th className="pb-1 pl-6 text-left font-normal">Top edit</th>
                <th className="pb-1 pl-6 text-left font-normal" title="Recurring reasons clinicians edit this prompt">
                  Themes
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedProduction.map(({ prompt_key, owner, production: p }) => {
                const themesOpen = expandedThemes.has(prompt_key);
                // A key is free text; an id can't hold whitespace.
                const themesId = `themes-${encodeURIComponent(prompt_key)}`;
                return (
                  <Fragment key={prompt_key}>
                    <tr className="border-t border-border-subtle">
                      <td className="whitespace-nowrap py-1.5 pr-6 align-top">
                        <PromptLink promptKey={prompt_key} />
                        <OwnerChip owner={owner} />
                      </td>
                      <td className="py-1.5 pl-2 text-right align-top text-text-secondary">
                        {p.sample_size.toLocaleString()}
                      </td>
                      <td className="py-1.5 pl-2 text-right align-top font-medium text-text-primary">
                        {formatPercent(p.pct_fields_edited)}
                      </td>
                      <td className="py-1.5 pl-2 text-right align-top text-text-secondary">
                        {formatPercent(p.avg_edit_percentage)}
                      </td>
                      <td className="py-1.5 pl-2 text-right align-top text-text-secondary">
                        {p.pct_fields_total_rewrite === null ? "—" : formatPercent(p.pct_fields_total_rewrite)}
                      </td>
                      <td className="py-1.5 pl-2 text-right align-top text-text-secondary">
                        {p.avg_quality_score.toFixed(2)}
                      </td>
                      <td className="py-1.5 pl-6 align-top text-text-secondary">
                        {p.top_category &&
                        (p.top_category_description ||
                          p.top_category_prompt_action ||
                          p.top_category_examples.length > 0) ? (
                          <CategoryExamples
                            category={p.top_category}
                            description={p.top_category_description}
                            promptAction={p.top_category_prompt_action}
                            examples={p.top_category_examples}
                          />
                        ) : (
                          (p.top_category ?? "—")
                        )}
                      </td>
                      <td className="whitespace-nowrap py-1.5 pl-6 align-top text-text-secondary">
                        {p.themes.length > 0 ? (
                          <button
                            type="button"
                            onClick={() => toggleThemes(prompt_key)}
                            aria-expanded={themesOpen}
                            aria-controls={themesId}
                            className="hover:text-text-primary"
                          >
                            <span className="text-xs text-text-tertiary">{themesOpen ? "▾" : "▸"}</span>{" "}
                            {p.themes.length} {p.themes.length === 1 ? "theme" : "themes"}
                          </button>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                    {themesOpen ? (
                      <tr id={themesId}>
                        <td colSpan={8} className="pb-3 pt-1">
                          <div className="grid grid-cols-1 gap-2 rounded-md bg-bg-default p-2 xl:grid-cols-2">
                            {p.themes.map((theme, i) => (
                              <ThemeCard key={`${theme.title}-${i}`} theme={theme} orgScoped={orgScoped} />
                            ))}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <Paragraph displayType="normal" size="small" className="mt-3 text-text-tertiary">
          Clinicians editing real reports created in the {METRICS_WINDOW_LABEL}. Groups under 20 scored fields are left
          out. Themes are found weekly {orgScoped ? "in this organization's reports" : "across organizations"}
          {templateId !== null ? " and span every template — the template filter doesn't narrow them" : ""}.
        </Paragraph>
      </section>
    </div>
  );
};

/** Page shell: scope filters + results. Filters live here, outside the
 *  results' loading/empty returns, so they stay usable while data changes.
 *  Model Testing lives at /model-testing. */
export const EvalResults: FC = () => {
  // In the URL, so a suggestion's Back to Eval Results returns to its org.
  const [searchParams, setSearchParams] = useSearchParams();
  const orgId = idParam(searchParams.get(EVAL_RESULTS_ORG_PARAM));
  const templateId = idParam(searchParams.get(EVAL_RESULTS_TEMPLATE_PARAM));
  // One update per change: two in a row would each start from the same URL.
  const setScope = (scope: { org: number | null; template: number | null }) => {
    const next = new URLSearchParams();
    if (scope.org !== null) next.set(EVAL_RESULTS_ORG_PARAM, String(scope.org));
    if (scope.template !== null) next.set(EVAL_RESULTS_TEMPLATE_PARAM, String(scope.template));
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="flex min-h-full flex-col px-16 pb-16 pt-8">
      <div className="mb-6">
        <ScopeFilters
          orgId={orgId}
          templateId={templateId}
          onOrgChange={(org) => setScope({ org, template: null })}
          onTemplateChange={(template) => setScope({ org: orgId, template })}
        />
      </div>
      <div className="flex-1">
        <ResultsTab orgId={orgId} templateId={templateId} />
      </div>
    </div>
  );
};
